import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Columns, ArrowUp, ArrowDown, BookmarkSimple, Trash, CaretDown } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "../lib/api";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Checkbox } from "./ui/checkbox";
import { Input } from "./ui/input";

export const isHideable = (c) => !!c.header && c.key !== "actions" && !c.fixed;

// Per-user default column layout for a list, saved to the login.
export const useColumnLayout = (viewKey) => {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["user-layouts"], queryFn: async () => (await api.get("/user-layouts")).data, enabled: !!viewKey, staleTime: 60000 });
  const save = useMutation({
    mutationFn: async (columns) => (await api.put(`/user-layouts/${viewKey}`, { columns })).data,
    onMutate: (columns) => qc.setQueryData(["user-layouts"], (old) => ({ ...(old || {}), [viewKey]: columns })),
  });
  return { layout: viewKey ? (data || {})[viewKey] || null : null, saveLayout: (cols) => save.mutate(cols) };
};

export const ColumnsMenu = ({ allCols, visibleKeys, onChange, onReset }) => {
  const hideable = allCols.filter(isHideable);
  const ordered = [...visibleKeys.map((k) => hideable.find((c) => c.key === k)).filter(Boolean), ...hideable.filter((c) => !visibleKeys.includes(c.key))];
  const toggle = (k) => onChange(visibleKeys.includes(k) ? visibleKeys.filter((x) => x !== k) : [...visibleKeys, k]);
  const move = (k, d) => {
    const i = visibleKeys.indexOf(k);
    const j = i + d;
    if (i < 0 || j < 0 || j >= visibleKeys.length) { return; }
    const next = [...visibleKeys];
    [next[i], next[j]] = [next[j], next[i]];
    onChange(next);
  };
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button className="h-8 px-2.5 inline-flex items-center gap-1.5 text-xs font-semibold border border-border rounded-sm hover:bg-muted transition-colors" data-testid="columns-menu-button"><Columns size={14} weight="bold" /> Columns</button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-72 p-2 rounded-sm" data-testid="columns-menu">
        <p className="text-xs font-semibold text-muted-foreground px-1 pb-1.5">Show, hide and order columns</p>
        <div className="max-h-80 overflow-y-auto space-y-0.5">
          {ordered.map((c) => {
            const on = visibleKeys.includes(c.key);
            return (
              <div key={c.key} className="flex items-center gap-2 px-1 py-1 rounded-sm hover:bg-muted/60" data-testid={`columns-item-${c.key}`}>
                <Checkbox checked={on} onCheckedChange={() => toggle(c.key)} data-testid={`columns-toggle-${c.key}`} />
                <span className={`flex-1 text-sm ${on ? "" : "text-muted-foreground"}`}>{c.header}</span>
                {on && <button onClick={() => move(c.key, -1)} className="p-0.5 text-muted-foreground hover:text-primary" data-testid={`columns-up-${c.key}`}><ArrowUp size={13} /></button>}
                {on && <button onClick={() => move(c.key, 1)} className="p-0.5 text-muted-foreground hover:text-primary" data-testid={`columns-down-${c.key}`}><ArrowDown size={13} /></button>}
              </div>
            );
          })}
        </div>
        <button onClick={onReset} className="mt-2 w-full text-xs text-primary hover:underline py-1" data-testid="columns-reset">Reset to default columns</button>
      </PopoverContent>
    </Popover>
  );
};

// Named views (columns + search/filters + sort) for lists without their own saved-views bar.
export const NamedViewsMenu = ({ viewKey, filters, sort, onApply }) => {
  const qc = useQueryClient();
  const [name, setName] = useState("");
  const { data: views = [] } = useQuery({ queryKey: ["saved-views", viewKey], queryFn: async () => (await api.get(`/saved-views?module=${viewKey}`)).data });
  const save = useMutation({
    mutationFn: async () => (await api.post("/saved-views", { name: name.trim(), module: viewKey, sort, filters })).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["saved-views", viewKey] }); setName(""); toast.success("View saved"); },
  });
  const del = useMutation({ mutationFn: async (id) => api.delete(`/saved-views/${id}`), onSuccess: () => qc.invalidateQueries({ queryKey: ["saved-views", viewKey] }) });
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button className="h-8 px-2.5 inline-flex items-center gap-1.5 text-xs font-semibold border border-border rounded-sm hover:bg-muted transition-colors" data-testid="named-views-button"><BookmarkSimple size={14} weight="bold" /> Views <CaretDown size={11} /></button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-72 p-2 rounded-sm" data-testid="named-views-menu">
        {views.length === 0 && <p className="text-xs text-muted-foreground px-1 py-1">No saved views yet.</p>}
        {views.map((v) => (
          <div key={v.id} className="flex items-center gap-1 rounded-sm hover:bg-muted/60" data-testid="named-view-item">
            <button onClick={() => onApply(v)} className="flex-1 text-left text-sm px-1.5 py-1.5" data-testid={`named-view-apply-${v.name}`}>{v.name}</button>
            <button onClick={() => del.mutate(v.id)} className="p-1 text-muted-foreground hover:text-destructive" data-testid="named-view-delete"><Trash size={13} /></button>
          </div>
        ))}
        <div className="flex gap-1.5 mt-2 pt-2 border-t border-border">
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Name this view" className="h-8 rounded-sm text-sm" data-testid="named-view-name" />
          <button onClick={() => save.mutate()} disabled={!name.trim() || save.isPending} className="h-8 px-2.5 text-xs font-semibold rounded-sm bg-primary text-primary-foreground disabled:opacity-40" data-testid="named-view-save">Save</button>
        </div>
      </PopoverContent>
    </Popover>
  );
};
